import { Euler, Matrix4, Vector3 } from '@math.gl/core';
import { v4 } from 'uuid';
import { Collider } from '../../colliders/Collider';
export var StencilOps;
(function (StencilOps) {
    StencilOps[StencilOps["KEEP"] = 0] = "KEEP";
    StencilOps[StencilOps["ZERO"] = 1] = "ZERO";
    StencilOps[StencilOps["REPLACE"] = 2] = "REPLACE";
    StencilOps[StencilOps["INCR"] = 3] = "INCR";
    StencilOps[StencilOps["INCR_WRAP"] = 4] = "INCR_WRAP";
    StencilOps[StencilOps["DECR_WRAP"] = 5] = "DECR_WRAP";
    StencilOps[StencilOps["INVERT"] = 6] = "INVERT";
})(StencilOps || (StencilOps = {}));
export var StencilTest;
(function (StencilTest) {
    StencilTest[StencilTest["NEVER"] = 0] = "NEVER";
    StencilTest[StencilTest["LESS"] = 1] = "LESS";
    StencilTest[StencilTest["GREATER"] = 2] = "GREATER";
    StencilTest[StencilTest["EQUAL"] = 3] = "EQUAL";
    StencilTest[StencilTest["ALWAYS"] = 4] = "ALWAYS";
    StencilTest[StencilTest["LEQUAL"] = 5] = "LEQUAL";
    StencilTest[StencilTest["GEQUAL"] = 6] = "GEQUAL";
    StencilTest[StencilTest["NOTEQUAL"] = 7] = "NOTEQUAL";
})(StencilTest || (StencilTest = {}));
/**
 * ヒエラルキーを構成するノードオブジェクト
 */
export class Node {
    position = new Vector3(0, 0, 0);
    rotation = new Euler(0, 0, 0);
    scale = new Vector3(1, 1, 1);
    matrixWorld = new Matrix4(Matrix4.IDENTITY);
    components = [];
    parent = null;
    children = [];
    visible = true;
    drawOrder = 0;
    stencilWrite = false;
    stencilOp = {
        fail: StencilOps.KEEP,
        zFail: StencilOps.KEEP,
        zPass: StencilOps.REPLACE
    };
    stencilFunc = {
        func: StencilTest.ALWAYS,
        ref: 0,
        mask: 0
    };
    stencilTest = false;
    uid = v4();
    name = 'Node';
    _lastPosition = new Vector3(0, 0, 0);
    _lastRotation = new Euler(0, 0, 0);
    _lastScale = new Vector3(1, 1, 1);
    _willUpdateMatrix = true;
    constructor(parent, name = 'Node') {
        this.name = name;
        if (parent) {
            parent.add(this);
        }
    }
    _collider = null;
    get collider() {
        if (!this._collider) {
            this._collider = this.getComponent(Collider);
        }
        return this._collider;
    }
    dispose() {
        this.parent?.remove(this);
    }
    updateMatrix(forceUpdate = false) {
        if (forceUpdate ||
            this._willUpdateMatrix ||
            !this.position.equals(this._lastPosition) ||
            !this.rotation.equals(this._lastRotation) ||
            !this.scale.equals(this._lastScale)) {
            const matrix = new Matrix4(Matrix4.IDENTITY);
            matrix.translate([this.position.x, this.position.y, this.position.z]);
            matrix.rotateXYZ([this.rotation.x, this.rotation.y, this.rotation.z]);
            matrix.scale([this.scale.x, this.scale.y, this.scale.z]);
            if (this.parent) {
                matrix.multiplyLeft(this.parent.matrixWorld);
            }
            this.matrixWorld = matrix;
            this._willUpdateMatrix = false;
            this._lastPosition.copy(this.position);
            this._lastRotation.copy(this.rotation);
            this._lastScale.copy(this.scale);
        }
    }
    traverse(callback) {
        const stack = [this];
        while (stack.length > 0) {
            const node = stack.pop();
            callback(node);
            for (let i = node.children.length - 1; i > -1; i--) {
                stack.push(node.children[i]);
            }
        }
    }
    add(node) {
        if (node.parent) {
            node.parent.remove(node);
        }
        let pNode = this.parent;
        while (pNode) {
            if (pNode === node) {
                throw new Error('Node cannot add ancestor node as its child');
            }
            pNode = pNode.parent;
        }
        this.children.push(node);
        node.parent = this;
    }
    remove(node) {
        for (let i = 0; i < this.children.length; i++) {
            if (this.children[i] === node) {
                this.children.splice(i, 1);
                node.parent = null;
                return;
            }
        }
    }
    draw(gl, pMatrix) {
        this.sendMessage(null, 'onDraw', gl, pMatrix);
    }
    update(dt) {
        return;
    }
    addComponent(ctor) {
        const component = new ctor(this);
        this.components.push(component);
        return component;
    }
    getComponent(ctor) {
        for (let i = 0; i < this.components.length; i++) {
            if (this.components[i] instanceof ctor) {
                return this.components[i];
            }
        }
        return null;
    }
    getComponents(ctor) {
        const components = [];
        for (let i = 0; i < this.components.length; i++) {
            if (this.components[i] instanceof ctor) {
                components.push(this.components[i]);
            }
        }
        return components;
    }
    getComponentInChildren(ctor) {
        let comp = this.getComponent(ctor);
        if (comp) {
            return comp;
        }
        for (let i = 0; i < this.children.length; i++) {
            comp = this.children[i].getComponentInChildren(ctor);
            if (comp) {
                return comp;
            }
        }
        return null;
    }
    getComponentsInChildren(ctor) {
        let comps = this.getComponents(ctor);
        this.traverse(node => {
            comps = comps.concat(this.getComponents(ctor));
        });
        return comps;
    }
    sendMessage(caller, event, ...arg) {
        for (let i = 0; i < this.components.length; i++) {
            const c = this.components[i];
            if (c.enabled && c[event] && c !== caller) {
                c[event](...arg);
            }
        }
    }
    _sendMessage(event, ...arg) {
        for (let i = 0; i < this.components.length; i++) {
            const c = this.components[i];
            if (c[event]) {
                c[event](...arg);
            }
        }
    }
}
//# sourceMappingURL=Node.js.map